export interface EpubChapter {
  id: string;
  title: string;
  filename: string;
  content: string;
  headings: Array<{ id: string; title: string; level: number }>;
}

export interface EpubTocEntry {
  id: string;
  title: string;
  filename: string;
  anchor?: string;
  level: number;
  children: EpubTocEntry[];
}

export interface ChapterDraft {
  title: string;
  nodes: any[];
  headings: Array<{ id: string; title: string; level: number }>;
}

export interface CommentReply {
  id: string;
  comment: string;
  userEmail?: string;
  createdAt?: string;
}

export interface Comment {
  id: string;
  selectedText?: string;
  content: string;
  userEmail?: string;
  replies?: CommentReply[];
}

export interface Suggestion {
  originalText: string;
  suggestionText: string;
  issueType: string;
  feedbackText: string;
}

export type PageNumberPosition = 'none' | 'bottom_left' | 'bottom_middle' | 'bottom_right';

export type LineSpacing = 'default' | 1 | 1.25 | 1.5 | 2;

export interface MarginsCm {
  top: number;
  right: number;
  bottom: number;
  left: number;
}

export const EXPORT_FONT_IDS = [
  'times_new_roman',
  'georgia',
  'garamond',
  'arial',
  'helvetica',
  'verdana',
] as const;

export type ExportFontId = (typeof EXPORT_FONT_IDS)[number];

export const EXPORT_FONT_SIZE_PTS = [10, 11, 12, 14, 16, 18] as const;

export type ExportFontSizePt = (typeof EXPORT_FONT_SIZE_PTS)[number];

export const DEFAULT_EXPORT_BODY_FONT_SIZE_PT: ExportFontSizePt = 12;

export interface ExportLayoutOptions {
  pageNumberPosition: PageNumberPosition;
  lineSpacing: LineSpacing;
  marginsCm: MarginsCm;
  exportFontId?: ExportFontId;
  exportFontSizePt?: ExportFontSizePt;
}

const WORD_FONT_BY_ID: Record<ExportFontId, string> = {
  times_new_roman: 'Times New Roman',
  georgia: 'Georgia',
  garamond: 'Garamond',
  arial: 'Arial',
  helvetica: 'Helvetica',
  verdana: 'Verdana',
};

const EPUB_BODY_FONT_BY_ID: Record<ExportFontId, string> = {
  times_new_roman: '"Times New Roman", Times, serif',
  georgia: 'Georgia, "Times New Roman", serif',
  garamond: 'Garamond, "Times New Roman", serif',
  arial: 'Arial, Helvetica, sans-serif',
  helvetica: 'Helvetica, Arial, sans-serif',
  verdana: 'Verdana, Geneva, sans-serif',
};

export type PdfExportFontFamily =
  | 'LiberationSerif'
  | 'LiberationSans'
  | 'Gelasio'
  | 'EBGaramond'
  | 'DejaVuSans';

export const getJsPdfFontFamily = (
  exportFontId: ExportFontId | undefined,
): PdfExportFontFamily => {
  if (!exportFontId) {
    return 'LiberationSerif';
  }
  switch (exportFontId) {
    case 'georgia':
      return 'Gelasio';
    case 'garamond':
      return 'EBGaramond';
    case 'arial':
    case 'helvetica':
      return 'LiberationSans';
    case 'verdana':
      return 'DejaVuSans';
    case 'times_new_roman':
    default:
      return 'LiberationSerif';
  }
};

export const getWordExportFontName = (exportFontId: ExportFontId | undefined): string | undefined => {
  if (!exportFontId) {
    return undefined;
  }
  return WORD_FONT_BY_ID[exportFontId];
};

export const getEpubBodyFontFamilyCss = (exportFontId: ExportFontId | undefined): string => {
  if (!exportFontId) {
    return '"Georgia", "Times New Roman", serif';
  }
  return EPUB_BODY_FONT_BY_ID[exportFontId];
};

export const getExportBodyFontSizePt = (
  exportFontSizePt: ExportFontSizePt | undefined,
): ExportFontSizePt => {
  if (exportFontSizePt == null) {
    return DEFAULT_EXPORT_BODY_FONT_SIZE_PT;
  }
  return exportFontSizePt;
};

export const getPdfHeadingFontSizePt = (
  level: number | undefined,
  bodyFontSizePt: number,
): number => {
  const scale = bodyFontSizePt / DEFAULT_EXPORT_BODY_FONT_SIZE_PT;
  if (level === 1) {
    return Math.round(16 * scale);
  }
  if (level === 2) {
    return Math.round(14 * scale);
  }
  return Math.round(DEFAULT_EXPORT_BODY_FONT_SIZE_PT * scale);
};

export interface ExportDocumentJsonSource {
  getJSON: () => unknown;
}

export interface ExportOptions {
  editor: any;
  ydoc?: any | null;
  documentTitle: string | null;
  projectTitle?: string | null;
  documentId: string | undefined;
  token: string | null;
  apiBase: string;
  exportComments: boolean;
  exportSuggestions: boolean;
  includeCoverPage?: boolean;
  coverAuthorName?: string;
  comments: Comment[];
  suggestions: Suggestion[];
  t: (key: string) => string;
  exportLayoutOptions?: ExportLayoutOptions;
  wordExportOptions?: ExportLayoutOptions;
}

export const resolveExportDocumentJson = (
  editor: ExportDocumentJsonSource | null | undefined,
  ydoc: unknown | null | undefined,
  ydocToJSON: (doc: any) => unknown | null,
): unknown | null => {
  if (ydoc) {
    const fromYdoc = ydocToJSON(ydoc);
    if (fromYdoc) {
      return fromYdoc;
    }
  }
  if (!editor) {
    return null;
  }
  return editor.getJSON();
};

export const resolveExportAppendixContent = (options: {
  exportComments: boolean;
  exportSuggestions: boolean;
  comments: Comment[];
  suggestions: Suggestion[];
}): { comments: Comment[]; suggestions: Suggestion[] } => {
  return {
    comments: options.exportComments ? options.comments : [],
    suggestions: options.exportSuggestions ? options.suggestions : [],
  };
};

export const getExportFilename = (documentTitle: string | null, projectTitle: string | null | undefined, extension: string): string => {
  const docTitle = documentTitle || 'proyecto';
  const projTitle = projectTitle || null;

  if (projTitle && projTitle !== docTitle) {
    return `${projTitle} - ${docTitle}.${extension}`;
  }
  return `${docTitle}.${extension}`;
};

export { exportToWord } from './export-word';
export { exportToPDF } from './export-pdf';
export { exportToEpub } from './export-epub';
